import { publicEnv } from '@/lib/config/public-env';
// === Next.js ===

// === Types & Interfaces ===
import type {
    AuthActionResult,
    AuthenticationResponse,
    ForgotPasswordRequest,
    FreemiumSignupRequest,
    FreemiumSignupResponse,
    OrganizationProfile,
    OrganizationProfileResponse,
    ResendVerificationEmailRequest,
    ResetPasswordRequest,
    SignInRequest,
    SignUpRequest,
    SubsidiaryOption,
    UserProfile,
    VerifyEmailRequest,
} from '@/@auth/types';
import type { ApiRequestInit } from '@/types';

// === Utils & Helpers ===
import { TeamMember } from '@/@account/types';
import { EVENTS } from '@/lib/features/tracking/events';
import { tracking } from '@/lib/features/tracking/tracking';
import type { AuthEventProps } from '@/lib/features/tracking/types';
import { portalApi } from '@/lib/http/axios';
import { AUTH_CONFIG } from '../config/config';

// === Constants ===
const API_URL = publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT');
/** Bound hung portal calls so SSR/HMR cannot block for axios's 5-minute default. */
const AUTH_FETCH_TIMEOUT_MS = process.env.NODE_ENV === 'development' ? 15_000 : 30_000;

/**
 * AuthService handles all authentication-related API calls and cookie management
 * This service provides methods for:
 * - User authentication (sign in, sign up)
 * - Password management (reset, forgot)
 * - Email verification
 * - Session management
 * - Token refresh and validation
 *
 * @class AuthService
 */
class AuthService {
    /**
     * Makes an authenticated request to the API
     * @param endpoint - API endpoint to call
     * @param options - Request options including method, body, headers
     * @returns Promise with the API response data
     * @throws Error if the request fails or if API_URL is not configured
     */
    private static async makeRequest<T>(endpoint: string, options: ApiRequestInit): Promise<T> {
        if (!API_URL) {
            throw new Error('API_URL is not configured');
        }

        tracking.event<AuthEventProps>(EVENTS.AUTH.API_REQUEST, {
            endpoint,
            method: options.method,
            hasHeaders: !!options.headers,
        });

        const startTime = Date.now();
        const response = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            signal: options.signal ?? AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
        });

        tracking.event<AuthEventProps>(EVENTS.AUTH.API_RESPONSE, {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            duration: Date.now() - startTime,
        });

        if (!response.ok) {
            const error = (await response.json()) as AuthActionResult;
            tracking.errorWithSource(
                new Error(error.error || 'Authentication failed'),
                'AuthService.makeRequest',
                { endpoint, status: response.status },
            );
            throw new Error(error.message || 'Authentication failed');
        }

        const data = (await response.json()) as T;
        tracking.event<AuthEventProps>(EVENTS.AUTH.API_SUCCESS, {
            endpoint,
            dataKeys: Object.keys(data as object),
            duration: Date.now() - startTime,
        });
        return data;
    }

    /**
     * Authenticates a user with email/password
     * @param credentials - User sign in credentials
     * @returns Promise with authentication tokens and user profile
     * @throws Error if authentication fails
     */
    static async signIn(credentials: SignInRequest): Promise<AuthenticationResponse> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SIGN_IN, { email: credentials.email });
        const response = await AuthService.makeRequest<{
            AuthenticationResult: AuthenticationResponse;
            user: UserProfile;
        }>('/studio/v1/auth/sign-in', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });

        tracking.event<AuthEventProps>(EVENTS.AUTH.AUTH_RESULT_STRUCTURE, {
            hasAuthResult: !!response.AuthenticationResult,
            authResultKeys: response.AuthenticationResult
                ? Object.keys(response.AuthenticationResult)
                : [],
            userKeys: response.user ? Object.keys(response.user) : [],
        });

        return {
            AccessToken: response.AuthenticationResult.AccessToken,
            RefreshToken: response.AuthenticationResult.RefreshToken,
            IdToken: response.AuthenticationResult.IdToken,
            ExpiresIn: response.AuthenticationResult.ExpiresIn,
            TokenType: response.AuthenticationResult.TokenType || 'Bearer',
            user: response.user,
        };
    }

    /**
     * Registers a new user
     * @param credentials - User registration details
     * @throws Error if registration fails
     */
    static async signUp(credentials: SignUpRequest): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SIGN_UP, { email: credentials.email });
        await AuthService.makeRequest('/studio/v1/auth/sign-up', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    static async freemiumSignup(request: FreemiumSignupRequest): Promise<FreemiumSignupResponse> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SIGN_UP, { email: request.email });
        return AuthService.makeRequest<FreemiumSignupResponse>('/studio/v1/users/freemium', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }

    /**
     * Registers a new user using an invite link
     * @param credentials - User registration details
     * @throws Error if registration fails
     */
    static async signUpWithInvite(credentials: SignUpRequest): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SIGN_UP, { email: credentials.email });
        await AuthService.makeRequest('/studio/v1/auth/signup-with-invite', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    /**
     * Initiates password reset flow
     * @param credentials - User email
     * @returns Object containing optional redirect URL
     * @throws Error if password reset initiation fails
     */
    static async forgotPassword(
        credentials: ForgotPasswordRequest,
    ): Promise<{ redirectUrl?: string }> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.FORGOT_PASSWORD, { email: credentials.email });
        return AuthService.makeRequest('/studio/v1/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    /**
     * Completes password reset with verification code
     * @param credentials - Reset credentials including code
     * @throws Error if password reset fails
     */
    static async resetPassword(credentials: ResetPasswordRequest): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.RESET_PASSWORD, { email: credentials.email });
        await AuthService.makeRequest('/studio/v1/auth/confirm-forgot-password', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    /**
     * Resends email verification
     * @param credentials - User email
     * @throws Error if resend fails
     */
    static async resendVerification(credentials: ResendVerificationEmailRequest): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.RESEND_VERIFICATION, {
            email: credentials.email,
        });
        await AuthService.makeRequest('/studio/v1/auth/resend-verification', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    /**
     * Verifies email with confirmation code
     * @param credentials - Email and confirmation code
     * @throws Error if verification fails
     */
    static async verifyEmail(credentials: VerifyEmailRequest): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.VERIFY_EMAIL, { email: credentials.email });
        await AuthService.makeRequest('/studio/v1/auth/confirm-email', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    // === Server-Side Methods ===

    /**
     * Sets authentication cookies
     * @param authResult - Authentication tokens to store
     */
    static async setAuthCookies(authResult: AuthenticationResponse): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SET_AUTH_COOKIES, {
            hasAccessToken: !!authResult.AccessToken,
            hasRefreshToken: !!authResult.RefreshToken,
            hasIdToken: !!authResult.IdToken,
            expiresIn: authResult.ExpiresIn,
            email: authResult.user?.email,
        });

        // No longer setting Cognito cookies as we're using NextAuth cookies only
        tracking.event<AuthEventProps>(EVENTS.AUTH.SKIP_COGNITO_COOKIE_SETTING, {});

        // NextAuth handles setting its own cookies, so we don't need to set any cookies here
        return;
    }

    /**
     * Clears all authentication cookies
     */
    static async clearAuthCookies(): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.CLEAR_AUTH_COOKIES, {});
        // No longer clearing Cognito cookies as we're using NextAuth cookies only
        tracking.event<AuthEventProps>(EVENTS.AUTH.SKIP_COGNITO_COOKIE_CLEARING, {});

        // NextAuth handles clearing its own cookies, so we don't need to clear any cookies here
        return;
    }

    /**
     * Retrieves current authentication cookies
     * @returns Object containing auth tokens from cookies
     */
    static async getAuthCookies(): Promise<{
        accessToken?: { value: string };
        refreshToken?: { value: string };
        idToken?: { value: string };
    }> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.GET_AUTH_COOKIES, {});
        // No longer getting Cognito cookies as we're using NextAuth cookies only
        tracking.event<AuthEventProps>(EVENTS.AUTH.SKIP_COGNITO_COOKIE_RETRIEVAL, {});

        // Return empty object since we're not using Cognito cookies anymore
        return {};
    }

    // === Development Mode Helpers ===

    /** Mock user for development testing */
    static readonly MOCK_USER = AUTH_CONFIG.development.mockUser;
    static readonly MOCK_ORG_PROFILE = AUTH_CONFIG.development.mockOrgProfile;
    static readonly MOCK_TEAM_MEMBER = AUTH_CONFIG.development.mockTeamMember;

    /**
     * Fetches user profile with ID token
     * @param idToken - Current ID token
     * @returns User profile data
     * @throws Error if profile fetch fails
     */
    static async getProfile(idToken: string): Promise<UserProfile> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.GET_PROFILE, {
            tokenLength: idToken?.length,
            tokenStart: idToken?.substring(0, 20) + '...',
            tokenType: 'ID Token',
        });

        return AuthService.makeRequest<UserProfile>('/studio/v1/auth/profile', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Authorization: `Bearer ${idToken}`,
                Accept: 'application/json',
            },
        });
    }

    static async getUserProfile(idToken: string): Promise<TeamMember> {
        return AuthService.makeRequest<TeamMember>('/studio/v1/users/me', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Authorization: `Bearer ${idToken}`,
                Accept: 'application/json',
            },
        });
    }

    static async getOrgProfile(idToken: string): Promise<OrganizationProfile> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.GET_ORG_PROFILE, {
            tokenLength: idToken?.length,
            tokenStart: idToken?.substring(0, 20) + '...',
            tokenType: 'ID Token',
        });

        const orgProfile = await AuthService.makeRequest<OrganizationProfileResponse>(
            '/studio/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    Accept: 'application/json',
                },
            },
        );

        return orgProfile.data;
    }

    /**
     * Checks if the user has Studio AI access
     * @param idToken - Current ID token
     * @returns Promise with access status
     * @throws Error if access check fails
     */
    static async getStudioAIAccess(idToken: string): Promise<{
        hasAccess: boolean;
        freemiumUsage?: {
            hasReachedLimit: boolean;
            currentCount: number;
            limit: number;
            limitType: 'message' | 'token';
            daysUntilReset: number | null;
            limitMessage: string | null;
        };
        clientStudioAIAccess?: {
            hasAccess: boolean;
            limitType: 'message' | 'token';
            limitValue: number;
            windowDays: number | null;
            currentCount: number;
            hasReachedLimit: boolean;
            daysUntilReset: number | null;
            limitMessage: string | null;
        };
    }> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.GET_PROFILE, {
            endpoint: 'studio-ai/access-status',
            tokenLength: idToken?.length,
        });

        return AuthService.makeRequest<{
            hasAccess: boolean;
            freemiumUsage?: {
                hasReachedLimit: boolean;
                currentCount: number;
                limit: number;
                limitType: 'message' | 'token';
                daysUntilReset: number | null;
                limitMessage: string | null;
            };
            clientStudioAIAccess?: {
                hasAccess: boolean;
                limitType: 'message' | 'token';
                limitValue: number;
                windowDays: number | null;
                currentCount: number;
                hasReachedLimit: boolean;
                daysUntilReset: number | null;
                limitMessage: string | null;
            };
        }>('/studio/v1/studio-ai/access-status', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Authorization: `Bearer ${idToken}`,
                Accept: 'application/json',
            },
        });
    }

    /**
     * Refreshes authentication tokens
     * @param refreshToken - Current refresh token
     * @returns New authentication tokens
     * @throws Error if token refresh fails
     */
    static async refreshToken(refreshToken: string): Promise<AuthenticationResponse> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.REFRESH_TOKEN, {});

        const response = await AuthService.makeRequest<{
            AuthenticationResult: AuthenticationResponse;
            user: UserProfile;
        }>('/studio/v1/auth/refresh-token', {
            method: 'POST',
            body: JSON.stringify({ refreshToken }),
        });

        return {
            ...response.AuthenticationResult,
            user: response.user,
        };
    }

    /**
     * Refreshes the current session using refresh token
     * @returns Promise with refreshed authentication result
     * @throws Error if session refresh fails
     */
    static async refreshSession(): Promise<AuthenticationResponse> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SESSION_REFRESH, {});
        const { refreshToken } = await AuthService.getAuthCookies();

        if (!refreshToken) {
            tracking.event<AuthEventProps>(EVENTS.AUTH.NO_REFRESH_TOKEN_FOUND, {});
            throw new Error('No refresh token found');
        }

        const authResult = await AuthService.refreshToken(refreshToken.value);
        await AuthService.setAuthCookies(authResult);
        return authResult;
    }

    /**
     * Signs out the current user and clears auth cookies
     * @throws Error if sign out fails
     */
    static async signOut(): Promise<void> {
        tracking.event<AuthEventProps>(EVENTS.AUTH.SIGN_OUT, {});
        await AuthService.clearAuthCookies();
    }

    /**
     * Subsidiary-scope options for the global filter (non-internal users).
     * Prefers a named `options` array when the portal API provides it; otherwise
     * zips orgIds + sfAccountIds from the scope payload.
     */
    static async getSubsidiaryOptions(): Promise<SubsidiaryOption[]> {
        const { data: response } = await portalApi.get<
            | {
                  options?: SubsidiaryOption[];
                  orgIds?: number[];
                  sfAccountIds?: string[];
              }
            | {
                  data?: {
                      options?: SubsidiaryOption[];
                      orgIds?: number[];
                      sfAccountIds?: string[];
                  };
              }
        >('/organization/subsidiaries');

        const data =
            response && typeof response === 'object' && 'data' in response && response.data
                ? response.data
                : (response as {
                      options?: SubsidiaryOption[];
                      orgIds?: number[];
                      sfAccountIds?: string[];
                  });

        if (data?.options?.length) {
            return data.options;
        }

        const orgIds = data?.orgIds ?? [];
        const sfAccountIds = data?.sfAccountIds ?? [];
        return orgIds.map((orgId, index) => ({
            orgId,
            sfAccountId: sfAccountIds[index] ?? '',
            orgName: `Organization ${orgId}`,
        }));
    }
}

export default AuthService;
